import { usePathname } from 'next/navigation';
import React, { useEffect } from 'react';

type RouterEventDetail = {
	destination: 'self' | 'newPathname';
};
export class DOPRouterEvent extends CustomEvent<RouterEventDetail> {
	constructor(detail: RouterEventDetail) {
		super('dop-router-navigate', { detail });
	}
}

export const triggerDOPRouterEvent = (detail: RouterEventDetail) => {
	const event = new DOPRouterEvent(detail);
	window.dispatchEvent(event);
};

export const useTriggerDOPRouterEvent = () => {
	const pathname = usePathname();
	const pathnameRef = React.useRef<string | undefined>(undefined);

	useEffect(() => {
		if (pathname === pathnameRef.current) return;

		triggerDOPRouterEvent({
			destination: 'newPathname',
		});

		pathnameRef.current = pathname;
	}, [pathname]);
};

/**
 * Attaches a handler to DOPRouterEvent events.
 * Returns a cleanup function to remove the event listener.
 * The cleanup function should be used as the cleanup function of a useEffect hook.
 */
export const onDOPRouterEvent = (handler: (event: DOPRouterEvent) => void) => {
	const controller = new AbortController();
	window.addEventListener(
		'dop-router-navigate',
		(event) => {
			if (event instanceof DOPRouterEvent) {
				handler(event);
			}
		},
		{
			signal: controller.signal,
		},
	);
	return () => {
		controller.abort();
	};
};
